import cn from "classnames";
import BookForm from "components/shared/book/form";
import { Transition } from "components/shared/column";
import { Datetime } from "components/shared/form";
import Loader from "components/shared/loader";
import { dateTimeToView } from "helpers/date";
import { trackerLogSourceTypes, trackerLogSourceTypesEnum } from "helpers/enums";
import { coordsToTimePoints } from "helpers/map";
import { formatNumber } from "helpers/system";
import moment from "moment";
import policy from "policies";
import React from "react";
import { getEmulatedSegments, removeTrackerLogs } from "sources/admin";
import { deleteEmulatedSegment } from "sources/books/trackers";
import { getTrackerLogs } from "sources/map";
import { config, initialState } from "./config";

class FormTrackerLogs extends BookForm {
    state = {
        isLoading: true,
        isValid: true,
        trackerLogsLoaded: false,
        distance: null,
    };

    componentWillUnmount() {
        this.resetMap();
    }

    resetMap = () => {
        const { mergeMapData } = this.props;
        mergeMapData({
            trackerCoords: null,
            trackerCoordsLoadedAt: null,
            trackerStops: null,
            emulatedSegments: null,
        });
    };

    setValue = ({ name, value, option }) => {
        let newState = { [name]: value };
        if (name === "trackerId") {
            const sourceTypeOption =
                option?.type === trackerLogSourceTypesEnum.Mobile
                    ? trackerLogSourceTypes.find((item) => item.value === trackerLogSourceTypesEnum.Mobile)
                    : trackerLogSourceTypes.find((item) => item.value === trackerLogSourceTypesEnum.Default);
            newState = { ...newState, sourceType: sourceTypeOption.intValue };
        }
        const isValid = this.isValidForm({ ...this.state, [name]: value });
        this.setState({ ...newState, trackerLogsLoaded: false, ...(isValid ? { isValid: true } : {}) });
    };

    loadLogs = () => {
        if (this.isValidForm(this.state)) {
            this.setState({ isLoading: true, isValid: true });
            const { trackerId, from, to } = this.state;
            const params = {
                trackerId,
                fixedAt: {
                    ge: new Date(from),
                    le: new Date(to),
                },
            };
            const { mergeMapData } = this.props;
            return Promise.all([getTrackerLogs(params), getEmulatedSegments({ trackerId, from, to })]).then(
                ([selfLogs, emulatedSegments]) => {
                    const { distance, trackerLogs } = selfLogs;
                    mergeMapData({
                        ...coordsToTimePoints(trackerLogs),
                        trackerCoordsLoadedAt: Date.now(),
                        emulatedSegments,
                    });
                    this.setState({ isLoading: false, trackerLogsLoaded: true, distance, emulatedSegments });
                },
            );
        } else {
            this.setState({ isValid: false });
        }
    };

    deleteLogs = () => {
        if (this.state.trackerLogsLoaded && this.isValidForm(this.state)) {
            this.props.modal({
                name: "confirm",
                title: "Удаление данных трекера",
                text: "Вы уверены, что хотите удалить данные трекера?",
                buttons: { confirm: "Удалить", cancel: "Отменить" },
                action: () => {
                    this.setState({ isLoading: true, isValid: true });
                    const { trackerId, from, to } = this.state;
                    const params = {
                        trackerId,
                        from: new Date(from),
                        to: new Date(to),
                    };
                    return removeTrackerLogs(params).then((data) => {
                        const { mergeMapData } = this.props;
                        if (data) {
                            this.removeEmulatedLogs();
                            mergeMapData({ trackerCoords: [], trackerStops: [], trackerCoordsLoadedAt: Date.now() });
                            this.setState({ trackerLogsLoaded: false, distance: null });
                        } else {
                            mergeMapData({ trackerCoords: [], trackerStops: [], trackerCoordsLoadedAt: Date.now() });
                            this.setState({ isLoading: false, trackerLogsLoaded: false, distance: null });
                        }
                    });
                },
            });
        } else {
            this.setState({ isValid: false });
        }
    };

    removeEmulatedLogs = () => {
        const { emulatedSegments } = this.state;
        const onSuccessReq = () => {
            const { mergeMapData } = this.props;
            mergeMapData({ emulatedSegments: null });
            this.setState({ isLoading: false, emulatedSegments: null });
            this.loadLogs();
        };
        if (emulatedSegments?.length) {
            const sendReq = (i) => {
                deleteEmulatedSegment({ id: emulatedSegments[i].id }).then(() => {
                    if (emulatedSegments[i + 1]) {
                        setTimeout(() => {
                            sendReq(i + 1);
                        }, 1000);
                    } else {
                        onSuccessReq();
                    }
                });
            };
            setTimeout(() => {
                sendReq(0);
            }, 1000);
        } else {
            onSuccessReq();
        }
    };

    renderDateField = (props) => {
        return (
            <Datetime
                {...props}
                onChange={this.setValue}
                value={this.state[props.inputProps.name]}
                additionalText={<span className="font-size-12 text-muted">({moment().format("Z")})</span>}
            />
        );
    };

    renderDistance = () => {
        const { distance } = this.state;
        if (!distance) {
            return null;
        }
        return (
            <div className="row form-group field-wrap--value">
                <label className="col-sm-4 col-md-3 col-form-label col-form-label-sm text-md-right text-sm-left">
                    Дистанция
                </label>
                <div className="col-sm-8 col-md-9 col-form-label col-form-label-sm">
                    {formatNumber(Math.round(distance / 1000))} км.
                    <span className="font-size-12 text-muted ml-1">(пробег не учитывает эмулированные координаты)</span>
                </div>
            </div>
        );
    };

    deleteEmulatedLog = (id) => {
        this.setState({ isLoading: true });
        deleteEmulatedSegment({ id }).then((res) => {
            if (res) {
                this.loadLogs();
            } else {
                this.setState({ isLoading: false });
            }
        });
    };

    deleteAllEmulatedLogs = () => {
        this.props.modal({
            name: "confirm",
            title: "Удаление сегментов",
            text: "Вы уверены, что хотите удалить все эмулированные сегменты?",
            buttons: { confirm: "Да", cancel: "Нет" },
            action: () => {
                this.setState({ isLoading: true });
                this.removeEmulatedLogs();
            },
        });
    };

    renderEmulatedSegmentsInfo = () => {
        const { emulatedSegments } = this.state;
        if (!emulatedSegments) {
            return null;
        }
        return (
            <div className="form-group field-wrap--value">
                <div className="d-flex justify-content-between pb-2">
                    Эмулированные сегменты
                    <div className="btn btn-xs btn-outline-danger" onClick={() => this.deleteAllEmulatedLogs()}>
                        Удалить все
                    </div>
                </div>
                <table className="table table-xs">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Период</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {emulatedSegments.map((item) => (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                    {dateTimeToView(item.from)}&nbsp;&mdash;&nbsp;{dateTimeToView(item.to)}
                                </td>
                                <td>
                                    <div
                                        className="btn btn-xs btn-outline-danger"
                                        onClick={() => this.deleteEmulatedLog(item.id)}
                                    >
                                        Удалить
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    render() {
        const { isValid, isLoading } = this.state;
        const { transition, roles, config } = this.props;
        const cname = cn("book-form__body", { "book-form__body--invalid": !isValid });
        return (
            <Transition dir={transition || "left"}>
                {isLoading ? (
                    <Loader prefix="absolute-center" />
                ) : (
                    <div>
                        {this.renderHead()}
                        <form className={cname}>
                            <div className="overflow-auto h-100">
                                <div className="p-3">
                                    {this.renderSelect(config.trackerId)}
                                    {this.renderDateField(config.from)}
                                    {this.renderDateField(config.to)}
                                    {this.renderDistance()}
                                    {this.renderEmulatedSegmentsInfo()}
                                </div>
                            </div>
                            <div className="p-3">
                                <div className="btn btn-brand btn-sm mr-2" onClick={this.loadLogs}>
                                    Посмотреть логи
                                </div>
                                {policy("user", { user: { roles } }).isAdminWithWriteAccess() &&
                                    this.state.trackerLogsLoaded && (
                                        <div className="btn btn-danger btn-sm" onClick={this.deleteLogs}>
                                            Удалить логи
                                        </div>
                                    )}
                            </div>
                        </form>
                    </div>
                )}
            </Transition>
        );
    }
}

const ConfiguredForm = (props) => <FormTrackerLogs {...props} config={config} initialState={initialState} />;

export default ConfiguredForm;
